import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useAuth } from "../../../context/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import { IoIosArrowRoundBack } from "react-icons/io";
import QuestionSection from "../../ui/QuestionSection/QuestionSection";
import CommonFooter from "../../CommonFooter/CommonFooter";
import CommonButton from "../../ui/CommonButton/CommonButton";
import classes from "./FacilitiesEnvironment.module.css";
import { env } from "../../../constants/env";
import axios from "axios";

const FacilitiesEnvironment = () => {
  const { labId } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [currentSet, setCurrentSet] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isfetching, setIsFetching] = useState(false);

  const userRole = user?.role;
  const [editable, setEditable] = useState(userRole === "ASSESSOR");

  const handleAnswerChange = (questionId, score, comment, points) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: { score, comment, points },
    }));
  };

  useEffect(() => {
    if (!labId) return;
    fetchAccreditation();
  }, [labId, token, user._id]);

  const fetchAccreditation = async () => {
    setIsFetching(true);
    try {
      const res = await axios.get(`${env.kataApiUrl}/api/accreditation`, {
        params: {
          labId,
          section: "Facilities Environment",
          name: "SADCAS F134-B",
        },
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data.success && res.data.accreditation) {
        const accreditation = res.data.accreditation;

        // Check if current user is the assigned auditor
        if (accreditation.auditorId !== user._id && userRole !== "ASSESSOR") {
          setEditable(false);
        }

        if (accreditation.answers && accreditation.answers.length) {
          const mapped = accreditation.answers.reduce((acc, a) => {
            acc[a.questionId] = {
              score: a.score || "",
              comment: a.comment || "",
              points: a.points || 0,
            };
            return acc;
          }, {});
          setAnswers(mapped);
          setEditable(false); // ✅ answers exist → read-only
        } else {
          setEditable(true); // ✅ no answers → edit mode
        }
      }
    } catch (err) {
      console.error("Failed to fetch accreditation:", err);
    } finally {
      setIsFetching(false);
    }
  };

  const questionSets = [
    {
      questions1: [
        {
          questionId: "Q1",
          content:
            "a. Were the requirements for facilities and environmental conditions necessary for the performance of the laboratory activities specified, monitored and recorded?",
          number: "6.3.1",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
        {
          questionId: "Q2",
          content:
            "b. What measures are put in place to prevent cross-contamination, where examination procedures pose a risk, or where work can be affected or influenced by lack of separation?",
          number: "6.3.2 (c)",
          multiline: true,
          hideOptions: true,
          placeholder: "Add comment",
        },
        {
          questionId: "Q3",
          content:
            "c. Were patient samples and materials used in the examination processes stored in a manner that prevents cross contamination and deterioration?",
          number: "6.3.3 (b)",
          multiline: true,
          hideOptions: true,
          placeholder: "Add comment",
        },
        {
          questionId: "Q4",
          content:
            "d. Were storage and disposal facilities for hazardous materials and biological waste appropriate to the classification of the materials in the context of any statutory or regularory requirements?",
          number: "6.3.3 (c)",
          multiline: true,
          hideOptions: true,
          placeholder: "Add comment",
        },
      ],
      questions2: [
        {
          questionId: "Comment1",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
      ],
      render: (onAnswerChange, answers) => (
        <>
          <QuestionSection
            topQuestion={false}
            accreditation
            addWidth
            showPointsField={false}
            questions={questionSets[0].questions1}
            onAnswerChange={editable ? onAnswerChange : () => {}}
            answers={answers}
            disabled={!editable}
          />
          <QuestionSection
            topQuestion={false}
            showQuestion={false}
            showEmptyNumber={false}
            accreditation
            showPointsField={false}
            questions={questionSets[0].questions2}
            onAnswerChange={editable ? onAnswerChange : () => {}}
            answers={answers}
            disabled={!editable}
          />
        </>
      ),
    },
  ];

  const handleNext = () => {
    if (currentSet < questionSets.length - 1) setCurrentSet((prev) => prev + 1);
  };

  const handlePrevious = () => {
    if (currentSet > 0) setCurrentSet((prev) => prev - 1);
  };

  const handleDone = async () => {
    setIsSubmitting(true);
    try {
      const allQuestions = questionSets.flatMap((set) =>
        Object.keys(set)
          .filter((k) => k.startsWith("questions"))
          .flatMap((k) => set[k])
      );

      const answersArray = Object.entries(answers).map(([questionId, data]) => {
        const questionContent =
          allQuestions.find((q) => q.questionId === questionId)?.content || "";

        return {
          questionId,
          content: questionContent,
          score: data.score || undefined,
          comment: data.comment,
          points: data.points,
        };
      });

      const response = await fetch(`${env.kataApiUrl}/api/accreditation`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: "SADCAS F134-B",
          labId,
          section: "Facilities Environment",
          auditorId: user._id,
          answers: answersArray,
        }),
      });

      if (!response.ok) throw new Error("Submission failed");
      fetchAccreditation();
      toast.success("Updated successfully!");
    } catch (error) {
      toast.error(error.message || "Failed to submit assessment");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isfetching) {
    return <div className={classes.loading}>Loading...</div>;
  }

  return (
    <div
      className={`${classes.wrapper} ${!editable ? classes.fromParams : ""}`}
    >
      <div className="d-flex justify-content-between align-items-center">
        <div
          className="d-flex align-items-center gap-2 mb-4"
          onClick={() => navigate(-1)}
          role="button"
        >
          <IoIosArrowRoundBack size={35} />
          <h2 className="m-0">Clause 6.3: Facilities and environment</h2>
        </div>

        {userRole === "ASSESSOR" && !editable && (
          <CommonButton
            className={classes.editButton}
            onClick={() => setEditable(true)}
            title="Edit"
          />
        )}
      </div>

      {!editable ? (
        <div className={classes.infoText}>
          ✅ Your SADCAS B - Clause 6.3: Facilities and environment has been
          submitted.
        </div>
      ) : (
        questionSets[currentSet].render(handleAnswerChange, answers)
      )}

      {editable && (
        <CommonFooter
          handlePrevious={handlePrevious}
          handleNext={handleNext}
          handleDone={handleDone}
          cancelText="Cancel"
          handleCancel={() => setEditable(false)}
          currentSet={currentSet}
          questionSets={questionSets}
          doneText={isSubmitting ? "Submitting..." : "Update"}
          disabled={isSubmitting}
        />
      )}
    </div>
  );
};

export default FacilitiesEnvironment;
